import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiEdit2, FiVideo, FiChevronDown, FiChevronRight, FiCopy, FiX, FiLink, FiKey, FiHash, FiRotateCcw, FiBookOpen, FiMonitor, FiSliders, FiZap, FiRefreshCw } from 'react-icons/fi';
import {
  subscribeToZoomAccounts, createZoomAccount, updateZoomAccount, deleteZoomAccount,
  addMeetingToAccount, updateMeetingInAccount, deleteMeetingFromAccount,
  restoreMeetingFromTrash, permanentlyDeleteMeeting, emptyTrashForAccount, duplicateZoomAccount,
  addClaseMeetingToAccount, updateClaseMeetingInAccount, deleteClaseMeetingFromAccount,
  restoreClassMeetingFromTrash, permanentlyDeleteClassMeeting, emptyClassTrashForAccount
} from '../../services/zoomAccountsService';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/AuthContext';
import { getUserWorkspace } from '../../services/firestore';
import {
  subscribeToZoomConnections, routeAccountEmail, listWorkspaceCourses, createZoomRoom, applyZoomSettings, zoomErrorMessage,
  getZoomRoomSettings,
} from '../../services/zoomApiService';
import HxSelect from '../ui/HxSelect';
import ZoomApiConnectionsModal from './ZoomApiConnectionsModal';
import CreateZoomRoomModal from './CreateZoomRoomModal';
import ZoomSyncModal from './ZoomSyncModal';
import ZoomRoomSettingsModal from './ZoomRoomSettingsModal';

const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F97316', '#10B981', '#EF4444', '#06B6D4', '#84CC16'];

// ── Tab config ──
const TABS = [
  { id: 'masterclass', label: 'Reuniones Masterclass', icon: FiMonitor, color: 'purple', meetingsKey: 'meetings', deletedKey: 'deletedMeetings' },
  { id: 'clase', label: 'Reuniones Curso / Clase', icon: FiBookOpen, color: 'blue', meetingsKey: 'claseMeetings', deletedKey: 'deletedClassMeetings' }
];

// ── Add/Edit Account Modal ──
const AccountModal = ({ account, onSave, onClose }) => {
  const [name, setName] = useState(account?.name || '');
  const [email, setEmail] = useState(account?.email || '');
  const [color, setColor] = useState(account?.color || COLORS[0]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    onSave({ name: name.trim(), email: email.trim(), color });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">{account ? 'Editar Cuenta' : 'Nueva Cuenta Zoom'}</h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FiX size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre de la cuenta</label>
            <input autoFocus type="text" value={name} onChange={e => setName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              placeholder="Ej: Hingenia Master 4" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Email de la cuenta</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              placeholder="hingeniamaster4@instructores.hingenia.com" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Color identificador</label>
            <div className="flex gap-2">
              {COLORS.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full transition-all ${color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button type="submit" className="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
              {account ? 'Guardar' : 'Crear Cuenta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Mismo criterio que la Cloud Function: {curso} se reemplaza; sin él, se agrega " - curso".
const applyTopicTemplate = (template, courseName) => {
  const t = String(template || '').trim();
  if (!t) return courseName;
  return t.includes('{curso}') ? t.replace(/\{curso\}/g, courseName) : `${t} - ${courseName}`;
};

// Campos de la reunión que se pueden cambiar para una sala concreta.
const ROOM_TOGGLES = [
  ['joinBeforeHost', 'Permitir entrar antes que el anfitrión'],
  ['waitingRoom', 'Sala de espera'],
  ['muteUponEntry', 'Silenciar al entrar'],
  ['hostVideo', 'Video del anfitrión encendido'],
  ['participantVideo', 'Video de participantes encendido'],
];
const ROOM_FIELDS = ['password', 'autoRecording', ...ROOM_TOGGLES.map(([k]) => k)];

const FieldLabel = ({ text, changed }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1">
    {text}{changed && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">modificado</span>}
  </label>
);

// ── Add/Edit Meeting Modal ──
const MeetingModal = ({ meeting, tabId, accounts, fixedAccount, workspaceId, zoomConnections, onSave, onClose }) => {
  const [name, setName] = useState(meeting?.name || '');
  const [zoomLink, setZoomLink] = useState(meeting?.zoomLink || '');
  const [meetingId, setMeetingId] = useState(meeting?.meetingId || '');
  const [password, setPassword] = useState(meeting?.password || '');
  // Selector de cuenta solo cuando se agrega desde el botón de arriba (sin cuenta fija)
  const showAccountPicker = !fixedAccount && Array.isArray(accounts);
  const [accountId, setAccountId] = useState(fixedAccount?.id || accounts?.[0]?.id || '');

  // Crear en Zoom: solo al agregar y solo con cuentas vinculadas a una conexión Zoom API.
  const zoomAccounts = (accounts || []).filter(a => routeAccountEmail(a.email, zoomConnections));
  const fixedIsZoom = !fixedAccount || zoomAccounts.some(a => a.id === fixedAccount.id);
  const canCreateInZoom = !meeting && !!workspaceId && zoomAccounts.length > 0 && fixedIsZoom;
  const [mode, setMode] = useState(canCreateInZoom ? 'zoom' : 'manual');
  const [zoomAccountId, setZoomAccountId] = useState(
    (fixedAccount && fixedIsZoom ? fixedAccount.id : zoomAccounts[0]?.id) || '');
  const [courses, setCourses] = useState(null);
  const [courseId, setCourseId] = useState('');
  const [creating, setCreating] = useState(false);
  const [kind, setKind] = useState(tabId);
  // Configuración heredada de "Ajustes de creación" (base) y la de esta sala (cfg), editable solo para ella.
  const [base, setBase] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [topicDraft, setTopicDraft] = useState(null); // null = nombre automático según la plantilla

  useEffect(() => {
    if (mode !== 'zoom' || courses || !workspaceId) return;
    listWorkspaceCourses(workspaceId)
      .then(setCourses)
      .catch((err) => { console.error(err); setCourses([]); toast.error('No se pudieron cargar los cursos'); });
  }, [mode, courses, workspaceId]);

  useEffect(() => {
    if (mode !== 'zoom' || base || !workspaceId) return;
    getZoomRoomSettings(workspaceId)
      .then(s => { setBase(s); setCfg(s); })
      .catch((err) => { console.error(err); toast.error('No se pudieron cargar los Ajustes de creación'); });
  }, [mode, base, workspaceId]);

  const course = (courses || []).find(c => c.id === courseId);
  const autoTopic = base && course ? applyTopicTemplate(kind === 'masterclass' ? base.topicMC : base.topicClase, course.name) : '';
  const topic = topicDraft ?? autoTopic;
  const changed = (k) => base && cfg && cfg[k] !== base[k];
  const overrides = !base || !cfg ? null : {
    ...Object.fromEntries(ROOM_FIELDS.filter(changed).map(k => [k, cfg[k]])),
    ...(topicDraft !== null && topicDraft.trim() && topicDraft.trim() !== autoTopic ? { topic: topicDraft.trim() } : {}),
  };
  const nChanged = overrides ? Object.keys(overrides).length : 0;
  const resetCfg = () => { setCfg(base); setTopicDraft(null); };

  const isValidLink = (link) => !link || /^https?:\/\//i.test(link.trim());
  const linkWarning = zoomLink.trim() && !isValidLink(zoomLink);

  const handleCreateInZoom = async () => {
    if (!zoomAccountId) { toast.error('Selecciona una cuenta'); return; }
    if (!course) { toast.error('Selecciona un curso'); return; }
    if (cfg && !String(cfg.password || '').trim()) { toast.error('La contraseña no puede estar vacía'); return; }
    const kindLabel = kind === 'masterclass' ? 'Masterclass' : 'Clase';
    setCreating(true);
    try {
      const r = await createZoomRoom({
        zoomAccountDocId: zoomAccountId, courseId: course.id, courseName: course.name, kind,
        ...(nChanged ? { overrides } : {}),
      });
      if (r.created) {
        toast.success(`Sala de ${kindLabel} creada en Zoom: ${r.room.name}${kind !== tabId ? ` (está en la pestaña ${kindLabel})` : ''}`);
      } else {
        const holder = (accounts || []).find(a => a.id === r.accountId);
        toast(`Ya existía una sala de ${kindLabel} para este curso${holder ? ` en "${holder.name}"` : ''} (no se duplicó ni se cambió su configuración)`, { icon: 'ℹ️', duration: 6000 });
      }
      onClose();
    } catch (err) {
      toast.error(zoomErrorMessage(err), { duration: 7000 });
    } finally { setCreating(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'zoom') { handleCreateInZoom(); return; }
    if (!name.trim()) return;
    if (showAccountPicker && !accountId) { toast.error('Selecciona una cuenta'); return; }
    if (linkWarning && !window.confirm('El enlace no parece una URL válida. ¿Guardar de todas formas?')) return;
    onSave({ name: name.trim(), zoomLink: zoomLink.trim(), meetingId: meetingId.trim(), password: password.trim(), accountId });
  };

  const isClase = (mode === 'zoom' ? kind : tabId) === 'clase';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{meeting ? 'Editar Reunión' : 'Nueva Reunión'}</h3>
            <p className={`text-xs font-medium mt-0.5 ${isClase ? 'text-blue-600' : 'text-purple-600'}`}>
              {isClase ? '📚 Reunión de Curso / Clase' : '🎓 Reunión de Masterclass'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FiX size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto">
          {canCreateInZoom && (
            <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-xl">
              {[['zoom', 'Crear en Zoom'], ['manual', 'Pegar enlace existente']].map(([m, label]) => (
                <button key={m} type="button" onClick={() => setMode(m)}
                  className={`py-2 text-sm font-semibold rounded-lg transition-colors ${mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  {m === 'zoom' && <FiZap size={13} className="inline mr-1 -mt-0.5" />}{label}
                </button>
              ))}
            </div>
          )}

          {mode === 'zoom' ? (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Cuenta Zoom</label>
                <HxSelect className="full" minWidth={0} value={zoomAccountId} onChange={setZoomAccountId}
                  disabled={!!fixedAccount} searchPlaceholder="Buscar cuenta…" ariaLabel="Cuenta Zoom"
                  options={zoomAccounts.map(a => ({ value: a.id, label: a.name, sub: a.email, color: a.color }))} />
                <p className="text-xs text-gray-400 mt-1">Solo cuentas conectadas a Zoom API.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de sala</label>
                <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-xl">
                  {[['masterclass', 'Masterclass', 'text-purple-700'], ['clase', 'Clase', 'text-blue-700']].map(([k, label, tone]) => (
                    <button key={k} type="button" onClick={() => { setKind(k); setTopicDraft(null); }}
                      className={`py-1.5 text-sm font-semibold rounded-lg transition-colors ${kind === k ? `bg-white shadow-sm ${tone}` : 'text-gray-500 hover:text-gray-700'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Curso</label>
                <HxSelect className="full" minWidth={0} value={courseId} onChange={(v) => { setCourseId(v); setTopicDraft(null); }}
                  placeholder={courses === null ? 'Cargando cursos…' : 'Elige el curso'}
                  disabled={!courses || courses.length === 0} searchPlaceholder="Buscar curso…" ariaLabel="Curso" popWidth={380}
                  options={(courses || []).map(c => ({ value: c.id, label: c.name }))} />
              </div>

              <div className="border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50 rounded-t-xl">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Configuración de la sala</p>
                    <p className="text-[11px] text-gray-400">
                      {nChanged ? `${nChanged} cambio(s) solo para esta sala` : 'Heredada de Ajustes de creación'}
                    </p>
                  </div>
                  {nChanged > 0 && (
                    <button type="button" onClick={resetCfg} className="text-xs font-medium text-blue-600 hover:text-blue-700">Restablecer</button>
                  )}
                </div>
                {!cfg ? (
                  <p className="px-3 py-4 text-sm text-gray-400">Cargando ajustes…</p>
                ) : (
                  <div className="p-3 space-y-3">
                    <div>
                      <FieldLabel text="Nombre en Zoom" changed={topicDraft !== null && topicDraft.trim() !== autoTopic} />
                      <input type="text" value={topic} onChange={e => setTopicDraft(e.target.value)} maxLength={200}
                        placeholder={course ? '' : 'Elige un curso para ver el nombre'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none" />
                      {kind === 'masterclass' && topic && !/master\s*clas/i.test(topic) && (
                        <p className="text-[11px] text-amber-600 mt-1">Sin “MASTERCLASS” en el nombre, el bot puede clasificar sus grabaciones como clase.</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <FieldLabel text="Contraseña" changed={changed('password')} />
                        <input type="text" value={cfg.password} maxLength={10}
                          onChange={e => setCfg({ ...cfg, password: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none" />
                      </div>
                      <div>
                        <FieldLabel text="Grabación" changed={changed('autoRecording')} />
                        <select value={cfg.autoRecording} onChange={e => setCfg({ ...cfg, autoRecording: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none">
                          <option value="cloud">En la nube</option>
                          <option value="local">En la computadora</option>
                          <option value="none">Desactivada</option>
                        </select>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {ROOM_TOGGLES.map(([k, label]) => (
                        <label key={k} className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
                          <span className="text-sm text-gray-700">
                            {label}{changed(k) && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">modificado</span>}
                          </span>
                          <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={!!cfg[k]}
                            onChange={e => setCfg({ ...cfg, [k]: e.target.checked })} />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-gray-400">El link, ID y contraseña se guardan solos. Los cambios de aquí no modifican tus Ajustes de creación.</p>
            </>
          ) : (
          <>
          {showAccountPicker && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Cuenta Zoom</label>
              <select value={accountId} onChange={e => setAccountId(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none bg-white">
                {(accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <p className="text-xs text-gray-400 mt-1">La reunión se agregará a esta cuenta.</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre {isClase ? 'del Curso / Clase' : 'de la Masterclass'}</label>
            <input autoFocus type="text" value={name} onChange={e => setName(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              placeholder={isClase ? 'Ej: Diseño Estructural de Colegios' : 'Ej: Metrados BIM y Análisis de Datos BI'} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Enlace Zoom</label>
            <div className="relative">
              <FiLink size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${linkWarning ? 'text-amber-500' : 'text-gray-400'}`} />
              <input type="text" value={zoomLink} onChange={e => setZoomLink(e.target.value)}
                className={`w-full pl-9 pr-4 py-2.5 border rounded-xl text-sm focus:ring-2 outline-none transition-colors ${linkWarning ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-100 bg-amber-50/30' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'}`}
                placeholder="https://zoom.us/j/..." />
            </div>
            {linkWarning && <p className="text-xs text-amber-600 mt-1">Debe empezar con https://</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">ID de Reunión</label>
              <div className="relative">
                <FiHash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={meetingId} onChange={e => setMeetingId(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  placeholder="123 456 789" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Contraseña</label>
              <div className="relative">
                <FiKey size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                  placeholder="abc123" />
              </div>
            </div>
          </div>
          </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
            <button type="submit" disabled={creating}
              className={`px-5 py-2 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${mode === 'zoom' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {mode === 'zoom' ? (creating ? 'Creando en Zoom…' : 'Crear sala en Zoom') : (meeting ? 'Guardar' : 'Agregar Reunión')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const fmtSyncDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d) ? d.toLocaleString('es-PE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
};

// ── Meeting Row ──
const MeetingRow = ({ meeting, tabId, accountId, account, onEdit, onDelete, onSelect, onCopy }) => (
  <div className="flex items-center gap-3 px-4 py-3 hover:bg-white transition-colors group">
    <FiVideo size={14} className={`flex-shrink-0 ${tabId === 'clase' ? 'text-blue-400' : 'text-purple-400'}`} />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-800 truncate">
        {meeting.name}
        {meeting.source === 'zoom-api' && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-50 text-green-700">Zoom API</span>}
        {meeting.source === 'zoom-import' && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">Importada</span>}
        {meeting.source !== 'zoom-api' && meeting.lastSyncedAt && !meeting.zoomMissing && (
          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-50 text-green-700"
            title={`Zoom confirmó que existe con este link y contraseña (${fmtSyncDate(meeting.lastSyncedAt)})`}>✓ Verificado</span>
        )}
        {meeting.zoomMissing && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-50 text-red-700" title="Esta reunión ya no existe en Zoom. La distribución no la usa.">⚠ No existe en Zoom</span>}
      </p>
      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
        {meeting.zoomLink && (
          <span className={`text-[11px] truncate max-w-[200px] ${/^https?:\/\//i.test(meeting.zoomLink) ? 'text-blue-500' : 'text-amber-500'}`} title={meeting.zoomLink}>
            <FiLink size={9} className="inline mr-0.5" />{meeting.zoomLink}
          </span>
        )}
        {meeting.meetingId && <span className="text-[11px] text-gray-400">ID: {meeting.meetingId}</span>}
        {meeting.password && <span className="text-[11px] text-gray-400">Pass: {meeting.password}</span>}
      </div>
    </div>
    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      {onSelect && (
        <button onClick={() => onSelect(meeting, account)}
          className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
          Usar
        </button>
      )}
      <button onClick={() => onCopy(meeting.zoomLink || '')}
        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Copiar enlace">
        <FiCopy size={13} />
      </button>
      <button onClick={onEdit}
        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
        <FiEdit2 size={13} />
      </button>
      <button onClick={onDelete}
        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
        <FiTrash2 size={13} />
      </button>
    </div>
  </div>
);

// ── Main Component ──
const ZoomAccountsManager = ({ onClose, onSelectMeeting, initialTab = 'masterclass' }) => {
  const [accounts, setAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [expandedId, setExpandedId] = useState(null);
  const [accountModal, setAccountModal] = useState(null);
  const [meetingModal, setMeetingModal] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showTrash, setShowTrash] = useState(false);
  const { user } = useAuth();
  const [workspaceId, setWorkspaceId] = useState(null);
  const [zoomConnections, setZoomConnections] = useState([]);
  const [showConnections, setShowConnections] = useState(false);
  const [showRoomSettings, setShowRoomSettings] = useState(false);
  const [createRoomFor, setCreateRoomFor] = useState(null);
  const [syncFor, setSyncFor] = useState(null);
  const [applyingFor, setApplyingFor] = useState(null);

  // Aplica en Zoom los Ajustes de creación a las salas existentes (no toca link, ID ni contraseña).
  const handleApplySettings = async (account) => {
    const n = [...(account.meetings || []), ...(account.claseMeetings || [])].filter(m => !m.zoomMissing && String(m.meetingId || m.zoomNumericId || '').replace(/\D/g, '')).length;
    if (n === 0) { toast('Esta cuenta no tiene salas con ID de Zoom', { icon: 'ℹ️' }); return; }
    if (!window.confirm(`¿Aplicar los Ajustes de creación (grabación, entrar antes del anfitrión, sala de espera, silenciar, video) a las ${n} salas de "${account.name}" en Zoom?\n\nNo cambia link, ID ni contraseña.`)) return;
    setApplyingFor(account.id);
    const tid = toast.loading(`Aplicando ajustes a ${n} salas…`);
    try {
      const r = await applyZoomSettings(account.id);
      toast.success(`Ajustes aplicados a ${r.updated} de ${r.total} salas`, { id: tid });
      if (r.notFound.length) toast(`${r.notFound.length} sala(s) ya no existen en Zoom. Sincroniza la cuenta para marcarlas.`, { icon: '⚠️', duration: 7000 });
      if (r.failed.length) toast.error(`${r.failed.length} sala(s) con error: ${r.failed[0].name} — ${r.failed[0].error}`, { duration: 9000 });
    } catch (err) {
      toast.error(zoomErrorMessage(err), { id: tid, duration: 9000 });
    } finally { setApplyingFor(null); }
  };

  useEffect(() => {
    if (!user?.uid) return;
    let alive = true;
    getUserWorkspace(user.uid).then(ws => { if (alive) setWorkspaceId(ws); });
    return () => { alive = false; };
  }, [user?.uid]);

  useEffect(() => {
    if (!workspaceId) return;
    return subscribeToZoomConnections(workspaceId, setZoomConnections);
  }, [workspaceId]);

  useEffect(() => {
    const unsub = subscribeToZoomAccounts(setAccounts);
    return () => unsub();
  }, []);

  // ── Account handlers ──
  const handleCreateAccount = async (data) => {
    try { await createZoomAccount(data); toast.success('Cuenta creada'); setAccountModal(null); }
    catch { toast.error('Error al crear cuenta'); }
  };

  const handleUpdateAccount = async (data) => {
    try { await updateZoomAccount(accountModal.account.id, data); toast.success('Cuenta actualizada'); setAccountModal(null); }
    catch { toast.error('Error al actualizar'); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('¿Eliminar esta cuenta y todas sus reuniones?')) return;
    try { await deleteZoomAccount(id); toast.success('Cuenta eliminada'); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleDuplicateAccount = async (accountId) => {
    try { await duplicateZoomAccount(accountId); toast.success('Cuenta duplicada con todas sus reuniones'); }
    catch { toast.error('Error al duplicar cuenta'); }
  };

  // ── Meeting handlers (tab-aware) ──
  const handleAddMeeting = async (data) => {
    try {
      const { accountId: pickedId, ...meetingData } = data;
      const accountId = meetingModal.accountId || pickedId;
      const account = meetingModal.account || accounts.find(a => a.id === accountId);
      if (!accountId || !account) { toast.error('Selecciona una cuenta'); return; }
      if (activeTab === 'masterclass') await addMeetingToAccount(accountId, account, meetingData);
      else await addClaseMeetingToAccount(accountId, meetingData);
      toast.success(`Reunión agregada a ${account.name}`);
      setMeetingModal(null);
    } catch { toast.error('Error al agregar reunión'); }
  };

  const handleUpdateMeeting = async (data) => {
    try {
      const { accountId: _picked, ...meetingData } = data;
      const { accountId, account, meeting } = meetingModal;
      if (activeTab === 'masterclass') await updateMeetingInAccount(accountId, account, meeting.id, meetingData);
      else await updateClaseMeetingInAccount(accountId, meeting.id, meetingData);
      toast.success('Reunión actualizada');
      setMeetingModal(null);
    } catch { toast.error('Error al actualizar'); }
  };

  const handleDeleteMeeting = async (accountId, account, meetingId) => {
    try {
      if (activeTab === 'masterclass') await deleteMeetingFromAccount(accountId, account, meetingId);
      else await deleteClaseMeetingFromAccount(accountId, meetingId);
      toast.success('Reunión movida a papelera');
    } catch { toast.error('Error al eliminar'); }
  };

  // ── Trash handlers (tab-aware) ──
  const handleRestoreMeeting = async (accountId, meetingId, isClase) => {
    try {
      if (isClase) await restoreClassMeetingFromTrash(accountId, meetingId);
      else await restoreMeetingFromTrash(accountId, meetingId);
      toast.success('Reunión restaurada');
    } catch { toast.error('Error al restaurar'); }
  };

  const handlePermanentDelete = async (accountId, meetingId, isClase) => {
    if (!window.confirm('¿Eliminar permanentemente? Esta acción no se puede deshacer.')) return;
    try {
      if (isClase) await permanentlyDeleteClassMeeting(accountId, meetingId);
      else await permanentlyDeleteMeeting(accountId, meetingId);
      toast.success('Eliminada permanentemente');
    } catch { toast.error('Error al eliminar'); }
  };

  const handleEmptyTrash = async (accountId) => {
    if (!window.confirm('¿Vaciar toda la papelera de esta cuenta? No se puede deshacer.')) return;
    try {
      await emptyTrashForAccount(accountId);
      await emptyClassTrashForAccount(accountId);
      toast.success('Papelera vaciada');
    } catch { toast.error('Error al vaciar'); }
  };

  const handleSelectMeeting = (meeting, account) => {
    if (onSelectMeeting) {
      onSelectMeeting({ zoomLink: meeting.zoomLink, meetingId: meeting.meetingId, password: meeting.password, accountName: account.name, meetingName: meeting.name });
    }
  };

  // ── Derived data ──
  const tab = TABS.find(t => t.id === activeTab);
  const totalMC = accounts.reduce((s, a) => s + (a.meetings?.length || 0), 0);
  const totalCL = accounts.reduce((s, a) => s + (a.claseMeetings?.length || 0), 0);
  const totalTrashed = accounts.reduce((s, a) => s + (a.deletedMeetings?.length || 0) + (a.deletedClassMeetings?.length || 0), 0);

  const filteredAccounts = searchQuery
    ? accounts.map(a => ({
      ...a,
      meetings: (a.meetings || []).filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase())),
      claseMeetings: (a.claseMeetings || []).filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()))
    })).filter(a => {
      const matchAccount = a.name.toLowerCase().includes(searchQuery.toLowerCase()) || a.email.toLowerCase().includes(searchQuery.toLowerCase());
      return matchAccount || (activeTab === 'masterclass' ? a.meetings.length > 0 : a.claseMeetings.length > 0);
    })
    : accounts;

  const tabColor = activeTab === 'masterclass'
    ? { btn: 'bg-purple-600 hover:bg-purple-700', badge: 'bg-purple-100 text-purple-700', tab: 'border-purple-500 text-purple-700' }
    : { btn: 'bg-blue-600 hover:bg-blue-700', badge: 'bg-blue-100 text-blue-700', tab: 'border-blue-500 text-blue-700' };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-5 sticky top-0 z-20 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FiVideo className="text-blue-600" /> Cuentas Zoom
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {accounts.length} cuentas · <span className="text-purple-600 font-medium">{totalMC} masterclass</span> · <span className="text-blue-600 font-medium">{totalCL} clase</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowConnections(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
              title="Credenciales de Zoom API">
              <FiLink size={14} /> Conexión Zoom API
              {zoomConnections.length > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">{zoomConnections.length}</span>}
            </button>
            <button onClick={() => setShowRoomSettings(true)} disabled={!workspaceId}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors disabled:opacity-40">
              <FiSliders size={14} /> Ajustes de creación
            </button>
            {totalTrashed > 0 && (
              <button onClick={() => setShowTrash(!showTrash)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showTrash ? 'bg-red-50 text-red-600 border border-red-200' : 'text-gray-500 hover:bg-gray-100 border border-gray-200'}`}>
                <FiTrash2 size={14} /> Papelera ({totalTrashed})
              </button>
            )}
            {!showTrash && (
              <button onClick={() => setAccountModal({})} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-sm">
                <FiPlus size={16} /> Nueva Cuenta
              </button>
            )}
            {onClose && (
              <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FiX size={20} /></button>
            )}
          </div>
        </div>

        {/* Search */}
        {!showTrash && (
          <div className="relative mt-4 max-w-md">
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Buscar reunión o cuenta..."
              className="w-full pl-4 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-gray-50" />
          </div>
        )}

        {/* Tabs */}
        {!showTrash && (
          <div className="flex items-end justify-between mt-4 border-b border-gray-200">
            <div className="flex gap-0">
            {TABS.map(t => {
              const Icon = t.icon;
              const isActive = activeTab === t.id;
              const count = t.id === 'masterclass' ? totalMC : totalCL;
              return (
                <button key={t.id} onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${isActive
                    ? `border-${t.color}-500 text-${t.color}-700 ${t.id === 'masterclass' ? 'border-purple-500 text-purple-700' : 'border-blue-500 text-blue-700'}`
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}>
                  <Icon size={15} />
                  {t.label}
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? (t.id === 'masterclass' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700') : 'bg-gray-100 text-gray-500'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
            </div>
            <button
              onClick={() => setMeetingModal({ pickAccount: true })}
              disabled={accounts.length === 0}
              title={accounts.length === 0 ? 'Crea una cuenta primero' : 'Agregar reunión y elegir la cuenta'}
              className={`flex items-center gap-1.5 mb-1.5 px-3 py-1.5 text-sm font-semibold text-white rounded-lg shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tabColor.btn}`}>
              <FiPlus size={15} /> Agregar reunión de {activeTab === 'masterclass' ? 'Masterclass' : 'Clase'}
            </button>
          </div>
        )}
      </div>

      {/* ── TRASH VIEW ── */}
      {showTrash ? (
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <button onClick={() => setShowTrash(false)} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 mb-2">
            ← Volver a cuentas
          </button>
          {accounts.filter(a => (a.deletedMeetings?.length || 0) + (a.deletedClassMeetings?.length || 0) > 0).length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <FiTrash2 size={40} className="mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">Papelera vacía</p>
              <p className="text-sm">Las reuniones eliminadas aparecerán aquí</p>
            </div>
          ) : (
            accounts.filter(a => (a.deletedMeetings?.length || 0) + (a.deletedClassMeetings?.length || 0) > 0).map(account => {
              const allTrashed = [
                ...(account.deletedMeetings || []).map(m => ({ ...m, _isClase: false })),
                ...(account.deletedClassMeetings || []).map(m => ({ ...m, _isClase: true }))
              ].sort((a, b) => new Date(b.deletedAt) - new Date(a.deletedAt));
              return (
                <div key={account.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between p-4 bg-red-50/50 border-b border-red-100">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: account.color }} />
                      <div>
                        <h3 className="font-bold text-gray-800 text-sm">{account.name}</h3>
                        <p className="text-xs text-gray-500">{allTrashed.length} en papelera</p>
                      </div>
                    </div>
                    <button onClick={() => handleEmptyTrash(account.id)}
                      className="text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-100 px-2.5 py-1.5 rounded-lg transition-colors">
                      Vaciar papelera
                    </button>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {allTrashed.map(meeting => (
                      <div key={meeting.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors group">
                        <FiVideo size={14} className={`flex-shrink-0 ${meeting._isClase ? 'text-blue-300' : 'text-purple-300'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-500 truncate">{meeting.name}</p>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${meeting._isClase ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                              {meeting._isClase ? 'CLASE' : 'MC'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            {meeting.zoomLink && <span className="text-[11px] text-gray-400 truncate max-w-[200px]"><FiLink size={9} className="inline mr-0.5" />{meeting.zoomLink}</span>}
                            {meeting.meetingId && <span className="text-[11px] text-gray-400">ID: {meeting.meetingId}</span>}
                            {meeting.deletedAt && (
                              <span className="text-[10px] text-red-400">
                                Eliminado: {new Date(meeting.deletedAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleRestoreMeeting(account.id, meeting.id, meeting._isClase)}
                            className="px-2.5 py-1 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors flex items-center gap-1">
                            <FiRotateCcw size={11} /> Restaurar
                          </button>
                          <button onClick={() => handlePermanentDelete(account.id, meeting.id, meeting._isClase)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar permanentemente">
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        /* ── MAIN ACCOUNTS VIEW ── */
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {filteredAccounts.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <FiVideo size={40} className="mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">No hay cuentas registradas</p>
              <p className="text-sm">Agrega tu primera cuenta Zoom para comenzar</p>
            </div>
          )}

          {filteredAccounts.map(account => {
            const isExpanded = expandedId === account.id;
            const meetings = activeTab === 'masterclass' ? (account.meetings || []) : (account.claseMeetings || []);
            const isEmpty = meetings.length === 0;

            return (
              <div key={account.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Account Header */}
                <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : account.id)}>
                  <div className="w-3 h-10 rounded-full flex-shrink-0" style={{ backgroundColor: account.color }} />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-800">{account.name}</h3>
                    <p className="text-xs text-gray-500 truncate flex items-center gap-1.5">
                      {account.email}
                      {zoomConnections.length > 0 && !routeAccountEmail(account.email, zoomConnections) && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" title="El email no es un usuario de ninguna conexión Zoom API">sin conexión API</span>
                      )}
                    </p>
                    {(account.zoomLastSyncAt || account.zoomSettingsAppliedAt) && (
                      <p className="text-[11px] text-gray-400 flex flex-wrap gap-x-3">
                        {account.zoomLastSyncAt && <span className="text-green-700">✓ Sincronizado con Zoom {fmtSyncDate(account.zoomLastSyncAt)}</span>}
                        {account.zoomSettingsAppliedAt && <span>Ajustes aplicados {fmtSyncDate(account.zoomSettingsAppliedAt)}</span>}
                      </p>
                    )}
                  </div>
                  {/* Counts for both tabs */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                      {(account.meetings || []).length} MC
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {(account.claseMeetings || []).length} CL
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {routeAccountEmail(account.email, zoomConnections) && (
                      <button onClick={(e) => { e.stopPropagation(); setSyncFor(account); }}
                        className="p-1.5 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors" title="Sincronizar con Zoom">
                        <FiRefreshCw size={14} />
                      </button>
                    )}
                    {routeAccountEmail(account.email, zoomConnections) && (
                      <button onClick={(e) => { e.stopPropagation(); handleApplySettings(account); }}
                        disabled={applyingFor === account.id}
                        className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Aplicar los Ajustes de creación a todas las salas de esta cuenta en Zoom">
                        <FiSliders size={14} className={applyingFor === account.id ? 'animate-pulse' : ''} />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); handleDuplicateAccount(account.id); }}
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Duplicar cuenta">
                      <FiCopy size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); setAccountModal({ account }); }}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      <FiEdit2 size={14} />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteAccount(account.id); }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <FiTrash2 size={14} />
                    </button>
                    {isExpanded ? <FiChevronDown size={18} className="text-gray-400" /> : <FiChevronRight size={18} className="text-gray-400" />}
                  </div>
                </div>

                {/* Meetings list */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50/50">
                    {isEmpty ? (
                      <div className="text-center py-6 text-gray-400 text-sm">
                        Sin reuniones de {activeTab === 'masterclass' ? 'Masterclass' : 'Clase'} en esta cuenta
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {meetings.map(meeting => (
                          <MeetingRow
                            key={meeting.id}
                            meeting={meeting}
                            tabId={activeTab}
                            accountId={account.id}
                            account={account}
                            onEdit={() => setMeetingModal({ accountId: account.id, account, meeting })}
                            onDelete={() => handleDeleteMeeting(account.id, account, meeting.id)}
                            onSelect={onSelectMeeting ? handleSelectMeeting : null}
                            onCopy={(link) => { navigator.clipboard.writeText(link); toast.success('Enlace copiado'); }}
                          />
                        ))}
                      </div>
                    )}
                    <div className="p-3 border-t border-gray-100 flex items-center gap-2">
                      <button onClick={() => setMeetingModal({ accountId: account.id, account })}
                        className={`flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${activeTab === 'masterclass'
                          ? 'text-purple-600 hover:text-purple-700 hover:bg-purple-50'
                          : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                          }`}>
                        <FiPlus size={14} /> Agregar reunión de {activeTab === 'masterclass' ? 'Masterclass' : 'Clase'}
                      </button>
                      {routeAccountEmail(account.email, zoomConnections) && (
                        <button onClick={() => setCreateRoomFor(account)}
                          className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors">
                          <FiZap size={14} /> Crear en Zoom
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {accountModal && (
        <AccountModal
          account={accountModal.account}
          onSave={accountModal.account ? handleUpdateAccount : handleCreateAccount}
          onClose={() => setAccountModal(null)}
        />
      )}
      {meetingModal && (
        <MeetingModal
          meeting={meetingModal.meeting}
          tabId={activeTab}
          accounts={accounts}
          fixedAccount={meetingModal.account}
          workspaceId={workspaceId}
          zoomConnections={zoomConnections}
          onSave={meetingModal.meeting ? handleUpdateMeeting : handleAddMeeting}
          onClose={() => setMeetingModal(null)}
        />
      )}
      {showConnections && <ZoomApiConnectionsModal connections={zoomConnections} onClose={() => setShowConnections(false)} />}
      {showRoomSettings && workspaceId && <ZoomRoomSettingsModal workspaceId={workspaceId} onClose={() => setShowRoomSettings(false)} />}
      {createRoomFor && workspaceId && (
        <CreateZoomRoomModal account={createRoomFor} accounts={accounts} kind={activeTab} workspaceId={workspaceId} onClose={() => setCreateRoomFor(null)} />
      )}
      {syncFor && <ZoomSyncModal account={syncFor} onClose={() => setSyncFor(null)} />}
    </div>
  );
};

export default ZoomAccountsManager;
